import { Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ReplayCircleFilledOutlinedIcon from '@mui/icons-material/ReplayCircleFilledOutlined';

import { StatusChip, tokens } from '../../../theme-next';
import { CgDocType } from '../ocr/ocrResponseTypes';

import type { ReceivedUpload } from './failedUploads';
import { type IdentityPerson, identityKindOf, identityPersonOf, identitySlotFor } from './identityRouting';
import { SimpleUploadZone } from './SimpleUploadZone';

export type DocRowStatus =
  | 'pending'
  | 'analyzing'
  | 'ok'
  | 'alerte'
  | 'verifier'
  | 'reupload'
  /** Pièce non exigible pour ce dossier (ex: CT sur un véhicule ≤ 4 ans). */
  | 'not_required'
  // ── Mode édition (chip "Pièces à déposer") ──
  /** Pièce demandée par GOD — l'agence doit la redéposer en priorité. */
  | 'requested'
  /** Pièce déjà acceptée par Skipcar — lecture seule, l'agence ne peut pas la modifier. */
  | 'validated_locked';

export interface DocRow {
  docType: CgDocType;
  title: string;
  subtitle: string;
  status: DocRowStatus;
  /** Affiché en tooltip pour le statut 'requested' (raison GOD du redepôt). */
  requestReason?: string | null;
  /** URL Firebase de la pièce occupant ce slot (clé de réassignation manuelle). */
  imageUrl?: string;
  /**
   * La PERSONNE de cette pièce a été choisie par défaut, faute de preuve
   * (verso muet, plusieurs personnes éligibles). On propose alors au déposant
   * de la rattacher lui-même.
   */
  personUncertain?: boolean;
}

interface DocumentChecklistColumnProps {
  rows: DocRow[];
  completedCount: number;
  totalCount: number;
  /** Si > 0, on est en mode édition — change le header pour montrer le compteur de pièces à redéposer. */
  requestedCount?: number;
  /**
   * Pièces déposées non rattachées à un slot (échecs à redéposer + pièces reçues
   * en plus : verso, doublon). Affichées dans le sous-bloc « Autres pièces
   * reçues » pour qu'aucun fichier envoyé ne disparaisse de l'écran.
   */
  receivedUploads?: ReceivedUpload[];

  // ── Co-titulaire (2 checkboxes + sous-bloc), sous « Autres pièces reçues ». ──
  /** Co-titulaire ACHETEUR (nouveau co-propriétaire) déclaré/détecté. */
  cotitulaireAcheteurActif?: boolean;
  onToggleCotitulaireAcheteur?: (checked: boolean) => void;
  /** Co-titulaire VENDEUR (C.4.1 carte grise) déclaré/détecté. */
  cotitulaireVendeurActif?: boolean;
  cotitulaireVendeurNom?: string | null;
  onToggleCotitulaireVendeur?: (checked: boolean) => void;
  /** Nom de fichier de la CNI du co-titulaire acheteur détectée (départage). */
  cotitulaireCniFileName?: string;
  /** Pièces d'identité du co-titulaire uploadées (sans OCR). */
  coBuyerFiles?: string[];
  onCoBuyerFilesChange?: (urls: string[]) => void;
  /** Rattachement manuel d'une pièce à une personne (lignes `personUncertain`). */
  onReassignIdentityPerson?: (imageUrl: string, person: IdentityPerson) => void;
}

interface StatusVisual {
  bg: string;
  fg: string;
  icon: string;
  chipStatus: 'autoValidated' | 'toVerify' | 'pending' | 'blocked' | 'notRequired';
}

const STATUS_VISUALS: Record<DocRowStatus, StatusVisual> = {
  ok: { bg: tokens.color.greenLight, fg: tokens.color.green, icon: '✓', chipStatus: 'autoValidated' },
  not_required: { bg: tokens.color.surface, fg: tokens.color.textMuted, icon: '—', chipStatus: 'notRequired' },
  alerte: { bg: tokens.color.orangeLight, fg: tokens.color.orange, icon: '!', chipStatus: 'toVerify' },
  verifier: { bg: tokens.color.amberLight, fg: tokens.color.amberDeep, icon: '!', chipStatus: 'toVerify' },
  reupload: { bg: tokens.color.redLight, fg: tokens.color.red, icon: '✗', chipStatus: 'blocked' },
  analyzing: { bg: tokens.color.blueLight, fg: tokens.color.blue, icon: '…', chipStatus: 'pending' },
  pending: { bg: tokens.color.surface, fg: tokens.color.textMuted, icon: '·', chipStatus: 'pending' },
  // Mode édition
  requested: { bg: tokens.color.orangeLight, fg: tokens.color.orangeDark, icon: '↻', chipStatus: 'toVerify' },
  validated_locked: {
    bg: tokens.color.greenLight,
    fg: tokens.color.green,
    icon: '🔒',
    chipStatus: 'autoValidated',
  },
};

/**
 * Lien de consultation d'une pièce, ouvert dans un nouvel onglet. Affiché sur
 * toute ligne rattachée à un fichier : la checklist nomme des documents, mais
 * sans accès au contenu on ne peut ni vérifier un rattachement, ni comprendre
 * un statut « à redéposer ».
 */
const OuvrirLaPiece = ({ url, label = 'Voir' }: { url: string; label?: string }) => (
  <Box
    component="a"
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e: React.MouseEvent) => e.stopPropagation()}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      flexShrink: 0,
      fontSize: '11px',
      fontWeight: 700,
      color: tokens.color.blue,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
    {label}
  </Box>
);

const PERSON_CHOICES: ReadonlyArray<{ value: IdentityPerson; label: string }> = [
  { value: 'titulaire', label: 'Titulaire' },
  { value: 'cotitulaire', label: 'Co-titulaire' },
  { value: 'hebergeur', label: 'Hébergeur' },
];

/**
 * Rattachement manuel d'une pièce à une personne, proposé UNIQUEMENT quand le
 * routage n'a pas pu trancher. Cas type : une CNI ancien format porte sa MRZ au
 * RECTO, si bien que son verso n'a ni nom ni n° de document ; quand un couple a
 * fait ses deux cartes le même mois, rien ne distingue plus les deux versos
 * (EA-035-SV). Aucun algorithme ne peut le savoir — le déposant, si.
 */
const PersonReassign = ({
  row,
  onReassign,
}: {
  row: DocRow;
  onReassign: (imageUrl: string, person: IdentityPerson) => void;
}) => {
  const current = identityPersonOf(row.docType);
  const kind = identityKindOf(row.docType);
  const face = row.docType.endsWith('_verso') ? 'verso' : 'recto';
  // On n'offre que les rôles qui ont réellement un slot pour cette nature de
  // pièce : le permis de l'hébergeur n'appartient pas au dossier CTVO, et le
  // proposer ferait disparaître la pièce de la checklist au clic.
  const choices = kind ? PERSON_CHOICES.filter((c) => identitySlotFor(kind, c.value, face) !== null) : [];
  if (choices.length < 2) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
      <Typography sx={{ fontSize: '11.5px', color: tokens.color.amberDeep, fontWeight: 700 }}>
        Cette face ne porte aucun nom — rattachée par défaut à :
      </Typography>
      {/* Sans un accès direct au fichier, la question est inutilisable : on
          demande de trancher sur une pièce que l'utilisateur ne voit pas
          (« je ne suis pas devin » — retour Luc 2026-07-30). */}
      {row.imageUrl && <OuvrirLaPiece url={row.imageUrl} label="Ouvrir la pièce" />}
      {choices.map((choice) => {
        const selected = choice.value === current;
        return (
          <Box
            key={choice.value}
            component="button"
            type="button"
            onClick={() => {
              if (!selected && row.imageUrl) onReassign(row.imageUrl, choice.value);
            }}
            sx={{
              cursor: selected ? 'default' : 'pointer',
              border: `1px solid ${selected ? tokens.color.amberDeep : tokens.color.border}`,
              backgroundColor: selected ? tokens.color.amberLight : tokens.color.white,
              color: selected ? tokens.color.amberDeep : tokens.color.textSecondary,
              fontWeight: 700,
              fontSize: '11px',
              padding: '3px 9px',
              borderRadius: '999px',
              lineHeight: 1.6,
              '&:hover': { borderColor: tokens.color.amberDeep },
            }}
          >
            {choice.label}
          </Box>
        );
      })}
    </Box>
  );
};

const Row = ({
  row,
  onReassignPerson,
}: {
  row: DocRow;
  onReassignPerson?: (imageUrl: string, person: IdentityPerson) => void;
}) => {
  const v = STATUS_VISUALS[row.status];
  const isAnalyzing = row.status === 'analyzing';
  const isOk = row.status === 'ok' || row.status === 'validated_locked';
  const isRejected = row.status === 'reupload';
  const isRequested = row.status === 'requested';
  const isLocked = row.status === 'validated_locked';
  const isNotRequired = row.status === 'not_required';

  // Animation de l'icône : pulse pendant l'analyse, pop quand OK, shake si rejet,
  // pulse léger d'attention si pièce à redéposer.
  const iconAnimationClass = isAnalyzing
    ? 'sk-pulse-dot'
    : isOk
      ? 'sk-pop-in'
      : isRejected
        ? 'sk-shake'
        : isRequested
          ? 'sk-pulse-dot'
          : '';

  const rowAnimationClass = isOk ? 'sk-fade-in-up' : isRejected ? 'sk-shake' : '';

  // Background selon le statut — vert très subtil pour les pièces verrouillées,
  // orange wash pour les pièces à redéposer (pour les faire ressortir visuellement).
  const rowBg = isOk
    ? tokens.color.greenLight
    : isRequested
      ? tokens.color.orangeLight
      : 'transparent';

  const content = (
    <Box
      key={row.status}
      className={rowAnimationClass}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px 16px',
        borderLeft: isRequested
          ? `3px solid ${tokens.color.orange}`
          : isLocked
            ? `3px solid ${tokens.color.green}`
            : '3px solid transparent',
        borderBottom: `0.5px solid ${tokens.color.border}`,
        backgroundColor: rowBg,
        opacity: isLocked || isNotRequired ? 0.85 : 1,
        transition: 'background-color 400ms ease',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        key={`${row.docType}-${row.status}-icon`}
        className={iconAnimationClass}
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: v.bg,
          color: v.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: 800,
          flexShrink: 0,
          marginTop: '2px',
          transition: 'background-color 300ms ease, color 300ms ease',
        }}
      >
        {isLocked ? (
          <LockOutlinedIcon sx={{ fontSize: 13 }} />
        ) : isRequested ? (
          <ReplayCircleFilledOutlinedIcon sx={{ fontSize: 14 }} />
        ) : (
          v.icon
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.color.dark }}>
          {row.title}
          {isRequested && (
            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: 10,
                fontWeight: 800,
                color: tokens.color.orangeDark,
                backgroundColor: tokens.color.white,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                border: `1px solid ${tokens.color.orangeBorder}`,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              À redéposer
            </Typography>
          )}
          {isLocked && (
            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: 10,
                fontWeight: 800,
                color: tokens.color.green,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Validée
            </Typography>
          )}
        </Typography>
        {(row.subtitle || row.imageUrl) && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
            {row.subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: isRequested ? tokens.color.orangeDark : tokens.color.textMuted,
                  lineHeight: 1.4,
                  fontWeight: isRequested ? 600 : 400,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.subtitle}
              </Typography>
            )}
            {/* Consultation directe de la pièce depuis la checklist : sans elle,
                on ne sait pas de QUEL fichier une ligne parle (retour Luc
                2026-07-30). Présent dès qu'un fichier existe — y compris pour
                une pièce à redéposer ou en cours d'analyse. */}
            {row.imageUrl && <OuvrirLaPiece url={row.imageUrl} />}
          </Box>
        )}
        {row.personUncertain && row.imageUrl && onReassignPerson && (
          <PersonReassign row={row} onReassign={onReassignPerson} />
        )}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <StatusChip status={v.chipStatus} />
      </Box>
    </Box>
  );

  // Tooltip uniquement si on a une raison (statut 'requested' ou 'validated_locked')
  if (isRequested && row.requestReason) {
    return (
      <Tooltip
        title={row.requestReason}
        arrow
        placement="left"
        slotProps={{
          tooltip: {
            sx: {
              fontSize: 12,
              maxWidth: 320,
              backgroundColor: tokens.color.dark,
              color: tokens.color.white,
              padding: '10px 12px',
              lineHeight: 1.45,
            },
          },
        }}
      >
        {content}
      </Tooltip>
    );
  }
  if (isLocked) {
    return (
      <Tooltip
        title="Cette pièce a déjà été acceptée par Skipcar. Pour la modifier, contactez le support."
        arrow
        placement="left"
      >
        {content}
      </Tooltip>
    );
  }
  return content;
};

/**
 * Sous-bloc « Autres pièces reçues » — rendu SOUS la checklist, à l'intérieur de
 * la même carte Documents. Surface tous les fichiers déposés non rattachés à un
 * slot (cf. `selectReceivedUploads`) : les échecs (`failed`, rouge, à redéposer)
 * et les pièces reçues en plus (`extra`, vert discret, « bien reçu »). Sans lui,
 * un verso déposé en second fichier disparaissait de l'écran et l'agence croyait
 * qu'il manquait une pièce (retour Biwiz 2026-07).
 */
const ReceivedUploadsSection = ({ items }: { items: ReceivedUpload[] }) => {
  if (items.length === 0) return null;
  const failedCount = items.filter((it) => it.category === 'failed').length;

  return (
    <Box className="sk-fade-in-up" sx={{ borderTop: `0.5px solid ${tokens.color.border}` }}>
      <Box sx={{ padding: '10px 16px 4px' }}>
        <Typography variant="overline" sx={{ color: tokens.color.textMuted }}>
          Autres pièces reçues
        </Typography>
        {failedCount > 0 && (
          <Typography variant="caption" sx={{ display: 'block', color: tokens.color.red, fontWeight: 600 }}>
            {failedCount === 1
              ? '1 fichier à redéposer (non exploitable)'
              : `${failedCount} fichiers à redéposer (non exploitables)`}
          </Typography>
        )}
      </Box>
      {items.map((it) => {
        const isFailed = it.category === 'failed';
        return (
          <Box
            key={it.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 16px',
              borderTop: `0.5px solid ${tokens.color.border}`,
              backgroundColor: isFailed ? tokens.color.redLight : 'transparent',
            }}
          >
            {isFailed ? (
              <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: tokens.color.red, flexShrink: 0 }} />
            ) : (
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: tokens.color.green, flexShrink: 0 }} />
            )}
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 15, color: tokens.color.textMuted, flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: 700,
                color: tokens.color.dark,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={it.fileName}
            >
              {it.fileName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                flexShrink: 0,
                fontWeight: 700,
                color: isFailed ? tokens.color.red : tokens.color.green,
                whiteSpace: 'nowrap',
              }}
            >
              {it.reason}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Sous-bloc « Co-titulaire » — rendu SOUS « Autres pièces reçues », dans la même
 * carte Documents. Deux checkboxes (acheteur / vendeur) auto-cochées si l'IA a
 * détecté, et, quand un co-titulaire ACHETEUR est déclaré, un sous-bloc « Pièce
 * d'identité du co-titulaire » qui accueille sa CNI (départage) + une zone de
 * dépôt. Le même bloc, avec les mêmes valeurs, est répliqué dans /review.
 */
export const CotitulaireSection = ({
  acheteurActif,
  onToggleAcheteur,
  vendeurActif,
  vendeurNom,
  onToggleVendeur,
  cotitulaireCniFileName,
  coBuyerFiles,
  onCoBuyerFilesChange,
  readOnly,
  vendeurReadOnly,
}: {
  acheteurActif: boolean;
  onToggleAcheteur?: (checked: boolean) => void;
  vendeurActif: boolean;
  vendeurNom?: string | null;
  onToggleVendeur?: (checked: boolean) => void;
  /** La case « vendeur » seule en lecture (review : elle vient de la carte grise). */
  vendeurReadOnly?: boolean;
  cotitulaireCniFileName?: string;
  coBuyerFiles?: string[];
  onCoBuyerFilesChange?: (urls: string[]) => void;
  readOnly?: boolean;
}) => {
  return (
    <Box sx={{ borderTop: `0.5px solid ${tokens.color.border}`, padding: '10px 16px 14px' }}>
      <Typography variant="overline" sx={{ color: tokens.color.textMuted }}>
        Co-titulaire
      </Typography>

      {/* Co-titulaire ACHETEUR (nouveau co-propriétaire) → CoBuyer / CTVO */}
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={acheteurActif}
            disabled={readOnly}
            onChange={(e) => onToggleAcheteur?.(e.target.checked)}
            sx={{ color: tokens.color.purple, '&.Mui-checked': { color: tokens.color.purple } }}
          />
        }
        label={
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: tokens.color.dark }}>
              Co-titulaire acheteur (nouveau co-propriétaire)
            </Typography>
            <Typography sx={{ fontSize: '11.5px', color: tokens.color.textSecondary, lineHeight: 1.35 }}>
              Le véhicule est repris par DEUX personnes (couple, indivision). Enregistré à l'ANTS avec l'acheteur.
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', margin: '6px 0 0' }}
      />
      {acheteurActif && (
        <Box sx={{ marginTop: '8px', marginLeft: '4px' }}>
          <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: tokens.color.dark, marginBottom: '6px' }}>
            Pièce d'identité du co-titulaire
          </Typography>
          {cotitulaireCniFileName && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 10px',
                borderRadius: `${tokens.radius.md}px`,
                backgroundColor: tokens.color.white,
                border: `0.5px solid ${tokens.color.border}`,
                marginBottom: '6px',
              }}
            >
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: tokens.color.green, flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{ flex: 1, minWidth: 0, fontWeight: 700, color: tokens.color.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={cotitulaireCniFileName}
              >
                {cotitulaireCniFileName}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: tokens.color.green, whiteSpace: 'nowrap' }}>
                Rattachée au co-titulaire
              </Typography>
            </Box>
          )}
          {!readOnly && onCoBuyerFilesChange && (
            <SimpleUploadZone urls={coBuyerFiles ?? []} onChange={onCoBuyerFilesChange} pathPrefix="images" />
          )}
        </Box>
      )}

      {/* Co-titulaire VENDEUR (C.4.1 carte grise) → signature cession */}
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={vendeurActif}
            disabled={readOnly || vendeurReadOnly}
            onChange={(e) => onToggleVendeur?.(e.target.checked)}
            sx={{ color: tokens.color.textMuted, '&.Mui-checked': { color: tokens.color.dark } }}
          />
        }
        label={
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: tokens.color.dark }}>
              Co-titulaire vendeur (carte grise, C.4.1){vendeurNom ? ` — ${vendeurNom}` : ''}
            </Typography>
            <Typography sx={{ fontSize: '11.5px', color: tokens.color.textSecondary, lineHeight: 1.35 }}>
              Le véhicule VENDU a deux co-propriétaires (C.4.1) : il doit aussi signer la cession côté vendeur.
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', margin: '10px 0 0' }}
      />
    </Box>
  );
};

export const DocumentChecklistColumn = ({
  rows,
  completedCount,
  totalCount,
  requestedCount = 0,
  receivedUploads = [],
  cotitulaireAcheteurActif = false,
  onToggleCotitulaireAcheteur,
  cotitulaireVendeurActif = false,
  cotitulaireVendeurNom,
  onToggleCotitulaireVendeur,
  cotitulaireCniFileName,
  coBuyerFiles,
  onCoBuyerFilesChange,
  onReassignIdentityPerson,
}: DocumentChecklistColumnProps) => {
  const inEditMode = requestedCount > 0;
  // En mode édition, on compte les pièces "validées" depuis les rows
  // (statut `validated_locked`), pas depuis `completedCount` qui n'incrémente
  // que sur les pièces OK fraîchement redéposées. Sinon le header afficherait
  // "0 - N validées" = nombre négatif au premier render.
  const validatedLockedCount = rows.filter((r) => r.status === 'validated_locked').length;
  // Les pièces NON REQUISES pour ce dossier (ex. Contrôle technique quand le
  // véhicule a ≤ 4 ans ; permis/justif de domicile quand l'acheteur est une
  // personne morale) ne sont PAS affichées : une ligne « NON REQUIS » embrouille
  // l'agence pour rien (retour Luc 2026-07 : « si ya pas besoin, on n'affiche
  // rien »). Le compteur n'est pas impacté (totalCount exclut déjà les
  // not_required). Si l'agence dépose quand même la pièce, elle est classée et
  // réapparaît (statut ok) via la dropzone ; si GOD la demande, statut requested.
  const visibleRows = rows.filter((r) => r.status !== 'not_required');
  return (
    <Box
      sx={{
        backgroundColor: tokens.color.white,
        border: `0.5px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius.xl}px`,
        boxShadow: tokens.shadow.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `0.5px solid ${tokens.color.border}`,
        }}
      >
        <Typography variant="overline">Documents</Typography>
        <Typography
          variant="caption"
          sx={{
            color: inEditMode
              ? tokens.color.orangeDark
              : completedCount === totalCount
                ? tokens.color.green
                : tokens.color.textMuted,
            fontWeight: 700,
          }}
        >
          {inEditMode
            ? `${requestedCount} à redéposer · ${validatedLockedCount} validée${
                validatedLockedCount > 1 ? 's' : ''
              }`
            : `${completedCount} / ${totalCount} ${completedCount === totalCount ? 'complets' : 'reçus'}`}
        </Typography>
      </Box>
      {visibleRows.map((row) => (
        <Row key={row.docType} row={row} onReassignPerson={onReassignIdentityPerson} />
      ))}
      <ReceivedUploadsSection items={receivedUploads} />
      <CotitulaireSection
        acheteurActif={cotitulaireAcheteurActif}
        onToggleAcheteur={onToggleCotitulaireAcheteur}
        vendeurActif={cotitulaireVendeurActif}
        vendeurNom={cotitulaireVendeurNom}
        onToggleVendeur={onToggleCotitulaireVendeur}
        cotitulaireCniFileName={cotitulaireCniFileName}
        coBuyerFiles={coBuyerFiles}
        onCoBuyerFilesChange={onCoBuyerFilesChange}
      />
    </Box>
  );
};
